import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';
import Modal from '../../components/Modal';
import EntityCompliancePanel from '../components/EntityCompliancePanel';

const DEPARTMENTS = ['Operations', 'Admin/Office', 'Sales', 'Maintenance', 'Management'];

const EMPTY_FORM = {
  first_name: '', last_name: '', email: '', phone: '',
  department: '', job_title: '', hire_date: '', termination_date: '',
  employment_status: 'active', address: '', city: '', state: 'TX', zip: '',
  emergency_contact_name: '', emergency_contact_phone: '',
  date_of_birth: '', drivers_license_number: '', drivers_license_expiry: '',
  user_id: '', notes: '',
};

const statusColors = {
  active: '#10b981',
  inactive: '#6b7280',
  terminated: '#ef4444',
};

export default function EmployeeListPage() {
  const { can } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [search, setSearch] = useState('');
  const [filterDept, setFilterDept] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {};
      if (search) filters.search = search;
      if (filterDept) filters.department = filterDept;
      if (filterStatus) filters.status = filterStatus;
      const data = await api.getEmployees(filters);
      setEmployees(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load employees');
    }
    setLoading(false);
  }, [search, filterDept, filterStatus]);

  const loadUsers = useCallback(async () => {
    try {
      const data = await api.getUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch { /* ignore — users dropdown just won't populate */ }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { loadUsers(); }, [loadUsers]);

  function openAdd() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setError('');
    setShowModal(true);
  }

  function openEdit(emp) {
    setEditing(emp);
    setForm({
      first_name: emp.first_name || '',
      last_name: emp.last_name || '',
      email: emp.email || '',
      phone: emp.phone || '',
      department: emp.department || '',
      job_title: emp.job_title || '',
      hire_date: emp.hire_date || '',
      termination_date: emp.termination_date || '',
      employment_status: emp.employment_status || 'active',
      address: emp.address || '',
      city: emp.city || '',
      state: emp.state || 'TX',
      zip: emp.zip || '',
      emergency_contact_name: emp.emergency_contact_name || '',
      emergency_contact_phone: emp.emergency_contact_phone || '',
      date_of_birth: emp.date_of_birth || '',
      drivers_license_number: emp.drivers_license_number || '',
      drivers_license_expiry: emp.drivers_license_expiry || '',
      user_id: emp.user_id || '',
      notes: emp.notes || '',
    });
    setError('');
    setShowModal(true);
  }

  async function handleSave(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = { ...form, user_id: form.user_id || null };
      if (editing) {
        await api.updateEmployee(editing.id, payload);
        setSuccess('Employee updated');
      } else {
        await api.createEmployee(payload);
        setSuccess('Employee added');
      }
      setShowModal(false);
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to save employee');
    }
    setSaving(false);
  }

  async function handleDelete(id) {
    try {
      await api.deleteEmployee(id);
      setDeleteConfirm(null);
      setSuccess('Employee deactivated');
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to deactivate employee');
      setDeleteConfirm(null);
    }
  }

  function handleChange(e) {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  }

  // Users available for linking (not already linked to another employee, or current employee's user)
  const availableUsers = users.filter(u => {
    const linkedToOther = employees.some(emp => emp.user_id === u.id && (!editing || emp.id !== editing.id));
    return !linkedToOther;
  });

  if (!can('manage_admin')) {
    return <div className="page-content"><p>You do not have permission to access this page.</p></div>;
  }

  return (
    <div className="admin-page">
      <div className="maint-types__header">
        <h1>Employees</h1>
        <button className="btn btn-gold" onClick={openAdd}>+ Add Employee</button>
      </div>

      {error && !showModal && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Filters */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Search by name, email, phone..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="form-input"
          style={{ maxWidth: '280px' }}
        />
        <select value={filterDept} onChange={e => setFilterDept(e.target.value)} className="form-input" style={{ maxWidth: '180px' }}>
          <option value="">All Departments</option>
          {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="form-input" style={{ maxWidth: '160px' }}>
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="terminated">Terminated</option>
        </select>
      </div>

      {loading ? (
        <div className="loading-spinner">Loading employees...</div>
      ) : (
        <div className="table-responsive">
          <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Department</th>
                <th>Job Title</th>
                <th className="hide-mobile">Phone</th>
                <th className="hide-mobile">Email</th>
                <th>Status</th>
                <th>Linked User</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {employees.length === 0 ? (
                <tr><td colSpan="8" className="text-center">No employees found. Add one to get started.</td></tr>
              ) : employees.map(emp => (
                <tr key={emp.id}>
                  <td><strong>{emp.first_name} {emp.last_name}</strong></td>
                  <td>{emp.department || '\u2014'}</td>
                  <td>{emp.job_title || '\u2014'}</td>
                  <td className="hide-mobile">{emp.phone || '\u2014'}</td>
                  <td className="hide-mobile">{emp.email || '\u2014'}</td>
                  <td>
                    <span className="status-pill" style={{
                      background: `${statusColors[emp.employment_status] || '#6b7280'}18`,
                      color: statusColors[emp.employment_status] || '#6b7280',
                    }}>
                      {emp.employment_status}
                    </span>
                  </td>
                  <td>
                    {emp.linked_username ? (
                      <span style={{ fontSize: '12px', color: '#3b82f6', fontWeight: 600 }}>
                        {emp.linked_username} ({emp.linked_role})
                      </span>
                    ) : (
                      <span style={{ fontSize: '12px', color: '#94a3b8' }}>Not linked</span>
                    )}
                  </td>
                  <td>
                    <button className="btn btn-sm btn-outline" onClick={() => openEdit(emp)}>Edit</button>
                    {' '}
                    {emp.employment_status !== 'terminated' && (
                      <button className="btn btn-sm btn-danger" onClick={() => setDeleteConfirm(emp)}>Deactivate</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <Modal title={editing ? 'Edit Employee' : 'Add Employee'} onClose={() => setShowModal(false)}>
          <form onSubmit={handleSave}>
            {error && <div className="alert alert-error" style={{ marginBottom: '16px' }}>{error}</div>}

            <div className="form-grid">
              <div className="form-group">
                <label>First Name *</label>
                <input name="first_name" value={form.first_name} onChange={handleChange} required className="form-input" />
              </div>
              <div className="form-group">
                <label>Last Name *</label>
                <input name="last_name" value={form.last_name} onChange={handleChange} required className="form-input" />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input name="email" type="email" value={form.email} onChange={handleChange} className="form-input" />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input name="phone" value={form.phone} onChange={handleChange} className="form-input" />
              </div>
              <div className="form-group">
                <label>Department</label>
                <select name="department" value={form.department} onChange={handleChange} className="form-input">
                  <option value="">Select...</option>
                  {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Job Title</label>
                <input name="job_title" value={form.job_title} onChange={handleChange} className="form-input" />
              </div>
              <div className="form-group">
                <label>Hire Date</label>
                <input name="hire_date" type="date" value={form.hire_date} onChange={handleChange} className="form-input" />
              </div>
              <div className="form-group">
                <label>Employment Status</label>
                <select name="employment_status" value={form.employment_status} onChange={handleChange} className="form-input">
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="terminated">Terminated</option>
                </select>
              </div>
              {form.employment_status === 'terminated' && (
                <div className="form-group">
                  <label>Termination Date</label>
                  <input name="termination_date" type="date" value={form.termination_date} onChange={handleChange} className="form-input" />
                </div>
              )}
              <div className="form-group">
                <label>Date of Birth</label>
                <input name="date_of_birth" type="date" value={form.date_of_birth} onChange={handleChange} className="form-input" />
              </div>
            </div>

            <h3 style={{ fontSize: '13px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b', margin: '20px 0 12px', borderTop: '1px solid #edf2f7', paddingTop: '16px' }}>Address</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Address</label>
                <input name="address" value={form.address} onChange={handleChange} className="form-input" />
              </div>
              <div className="form-group">
                <label>City</label>
                <input name="city" value={form.city} onChange={handleChange} className="form-input" />
              </div>
              <div className="form-group">
                <label>State</label>
                <input name="state" value={form.state} onChange={handleChange} className="form-input" />
              </div>
              <div className="form-group">
                <label>Zip</label>
                <input name="zip" value={form.zip} onChange={handleChange} className="form-input" />
              </div>
            </div>

            <h3 style={{ fontSize: '13px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b', margin: '20px 0 12px', borderTop: '1px solid #edf2f7', paddingTop: '16px' }}>Emergency Contact</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Contact Name</label>
                <input name="emergency_contact_name" value={form.emergency_contact_name} onChange={handleChange} className="form-input" />
              </div>
              <div className="form-group">
                <label>Contact Phone</label>
                <input name="emergency_contact_phone" value={form.emergency_contact_phone} onChange={handleChange} className="form-input" />
              </div>
            </div>

            <h3 style={{ fontSize: '13px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b', margin: '20px 0 12px', borderTop: '1px solid #edf2f7', paddingTop: '16px' }}>Driver's License</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>License Number</label>
                <input name="drivers_license_number" value={form.drivers_license_number} onChange={handleChange} className="form-input" />
              </div>
              <div className="form-group">
                <label>Expiration Date</label>
                <input name="drivers_license_expiry" type="date" value={form.drivers_license_expiry} onChange={handleChange} className="form-input" />
              </div>
            </div>

            <h3 style={{ fontSize: '13px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b', margin: '20px 0 12px', borderTop: '1px solid #edf2f7', paddingTop: '16px' }}>System Account</h3>
            <div className="form-group">
              <label>Link to User Account</label>
              <select name="user_id" value={form.user_id} onChange={handleChange} className="form-input">
                <option value="">No linked account</option>
                {availableUsers.map(u => (
                  <option key={u.id} value={u.id}>{u.full_name} ({u.username}) — {u.role}</option>
                ))}
              </select>
            </div>

            <div className="form-group" style={{ marginTop: '12px' }}>
              <label>Notes</label>
              <textarea name="notes" value={form.notes} onChange={handleChange} className="form-input" rows={3} />
            </div>

            <div className="modal-actions">
              <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-gold" disabled={saving}>
                {saving ? 'Saving...' : (editing ? 'Update Employee' : 'Add Employee')}
              </button>
            </div>
          </form>

          {editing && (
            <EntityCompliancePanel
              entityType="employee"
              entityId={editing.id}
              entityName={`${editing.first_name} ${editing.last_name}`}
            />
          )}
        </Modal>
      )}

      {/* Deactivate Confirmation */}
      {deleteConfirm && (
        <Modal title="Deactivate Employee" onClose={() => setDeleteConfirm(null)}>
          <p>Are you sure you want to deactivate <strong>{deleteConfirm.first_name} {deleteConfirm.last_name}</strong>?</p>
          <p style={{ fontSize: '13px', color: '#64748b' }}>Their record will be kept but marked as inactive.</p>
          <div className="modal-actions">
            <button className="btn btn-outline" onClick={() => setDeleteConfirm(null)}>Cancel</button>
            <button className="btn btn-danger" onClick={() => handleDelete(deleteConfirm.id)}>Deactivate</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
